import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Empty, Spinner } from "@/components/UIKit";
import StatusBadge from "@/components/StatusBadge";
import { fmtNOK, fmtDate, fmtKwh } from "@/lib/format";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Fakturering() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setItems(null);
    const params = new URLSearchParams({ limit: "200" });
    if (status) params.set("status", status);
    const { data } = await api.get(`/invoices?${params}`);
    setItems(data.items); setTotal(data.total);
  };

  useEffect(() => { load(); }, [status]);

  return (
    <div data-testid="fakturering-page">
      <PageHeader
        title="Fakturering"
        subtitle={`${total} fakturaer`}
        actions={
          <button
            onClick={() => setOpen(true)}
            className="h-9 px-3.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-2"
            data-testid="ny-faktura-btn"
          >
            <Plus className="h-4 w-4" /> Ny faktura
          </button>
        }
      />

      <div className="flex items-center gap-1 bg-zinc-900/60 rounded-md p-1 border border-zinc-900 mb-4 w-fit">
        {["", "utkast", "sendt", "betalt", "delvis_betalt", "purret", "kreditert"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded text-xs transition-colors ${status === s ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-200"}`}>
            {s || "Alle"}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        {items === null ? <div className="p-8"><Spinner /></div>
          : items.length === 0 ? <Empty title="Ingen fakturaer" subtitle="Opprett en ny faktura for å komme i gang" />
          : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-950/60 sticky top-0">
                <th className="py-2.5 px-4 font-medium">Fakturanr</th>
                <th className="py-2.5 px-4 font-medium">Kunde</th>
                <th className="py-2.5 px-4 font-medium">Dato</th>
                <th className="py-2.5 px-4 font-medium">Forfall</th>
                <th className="py-2.5 px-4 font-medium">Forbruk</th>
                <th className="py-2.5 px-4 font-medium text-right">Total</th>
                <th className="py-2.5 px-4 font-medium text-right">Betalt</th>
                <th className="py-2.5 px-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} onClick={() => navigate(`/fakturering/${i.id}`)}
                  className="border-t border-zinc-900 hover:bg-zinc-900/40 cursor-pointer" data-testid={`inv-row-${i.invoice_number}`}>
                  <td className="py-2.5 px-4 font-mono text-xs text-emerald-400">{i.invoice_number}</td>
                  <td className="py-2.5 px-4 text-zinc-100">{i.customer?.full_name || "—"}</td>
                  <td className="py-2.5 px-4 text-zinc-400">{fmtDate(i.issue_date)}</td>
                  <td className="py-2.5 px-4 text-zinc-400">{fmtDate(i.due_date)}</td>
                  <td className="py-2.5 px-4 tabular-nums text-zinc-300">{fmtKwh(i.consumption_kwh)}</td>
                  <td className="py-2.5 px-4 tabular-nums text-zinc-100 text-right">{fmtNOK(i.total_nok)}</td>
                  <td className="py-2.5 px-4 tabular-nums text-zinc-500 text-right">{fmtNOK(i.paid_nok || 0)}</td>
                  <td className="py-2.5 px-4"><StatusBadge value={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <NyFakturaDialog open={open} onOpenChange={setOpen} onCreated={(id) => { setOpen(false); navigate(`/fakturering/${id}`); }} />
    </div>
  );
}

function NyFakturaDialog({ open, onOpenChange, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    setFrom(first.toISOString().slice(0, 10));
    setTo(last.toISOString().slice(0, 10));
    api.get("/customers?limit=200&has_contract=true").then(({ data }) => setCustomers(data.items || []));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/invoices", {
        customer_id: customerId,
        period_from: new Date(from).toISOString(),
        period_to: new Date(to).toISOString(),
      });
      toast.success(`Faktura ${data.invoice_number} opprettet`);
      onCreated(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opprette");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900" data-testid="ny-faktura-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Ny faktura</DialogTitle>
          <DialogDescription className="sr-only">Generer en ny faktura for en kunde med aktiv avtale</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Kunde</label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required
              className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm"
              data-testid="dialog-customer">
              <option value="">— velg kunde med aktiv avtale —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{`${c.customer_number} · ${c.full_name}`}</option>
              ))}
            </select>
            {customers.length === 0 && (
              <div className="text-xs text-amber-400 mt-1">Ingen kunder med aktiv avtale funnet. Opprett en avtale under Avtaler eller på kundekortet først.</div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Periode fra</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required
                className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm" data-testid="dialog-from" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Periode til</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} required
                className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm" data-testid="dialog-to" />
            </div>
          </div>
          <button type="submit" disabled={busy || !customerId}
            className="w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="dialog-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Generer faktura
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
